import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Popover,
  Typography,
  List,
  ListItemButton,
  IconButton,
  alpha,
  useTheme,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { CalendarToday, ArrowDropDown, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  startOfYear,
  endOfYear,
  addMonths,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  parseISO,
  isBefore,
  isValid,
  startOfDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

export type PresetKey = 'total' | 'esta_semana' | 'este_mes' | 'ultimos_30_dias' | 'ultimos_3_meses' | 'ultimos_6_meses' | 'este_ano' | 'personalizado';

export interface FiltroPorPeriodoValue {
  start: string | null;
  end: string | null;
  preset?: PresetKey;
}

const PRESET_CONFIG: Array<{ key: PresetKey; label: string; getRange: (today: Date) => { start: Date; end: Date } | null }> = [
  { key: 'total', label: 'TOTAL', getRange: () => null },
  { key: 'esta_semana', label: 'ESTA SEMANA', getRange: (d) => ({ start: startOfWeek(d, { locale: ptBR }), end: endOfWeek(d, { locale: ptBR }) }) },
  { key: 'este_mes', label: 'ESTE MÊS', getRange: (d) => ({ start: startOfMonth(d), end: d }) },
  { key: 'ultimos_30_dias', label: 'ÚLTIMOS 30 DIAS', getRange: (d) => ({ start: startOfDay(subDays(d, 30)), end: d }) },
  { key: 'ultimos_3_meses', label: 'ÚLTIMOS 3 MESES', getRange: (d) => ({ start: startOfDay(subMonths(d, 3)), end: d }) },
  { key: 'ultimos_6_meses', label: 'ÚLTIMOS 6 MESES', getRange: (d) => ({ start: startOfDay(subMonths(d, 6)), end: d }) },
  { key: 'este_ano', label: 'ESTE ANO', getRange: (d) => ({ start: startOfYear(d), end: endOfYear(d) }) },
  { key: 'personalizado', label: 'PERSONALIZADO', getRange: () => null },
];

function toYMD(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function formatDisplay(d: string | null | undefined): string {
  // Sem esta guarda, remover um lado do período (start/end = null) fazia
  // format(parseISO(null)) lançar "Invalid time value" e quebrar a página.
  if (!d) return '';
  const parsed = parseISO(d);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy', { locale: ptBR }) : '';
}

export interface FiltroPorPeriodoProps {
  value: FiltroPorPeriodoValue;
  onChange: (value: FiltroPorPeriodoValue) => void;
  label?: string;
  /** Se true, ao abrir o popover não aplica na hora; usuário clica APLICAR (padrão Plataforma Verde) */
  applyOnClose?: boolean;
  /** Layout reduzido para cabeçalhos de gráficos */
  compact?: boolean;
  /** Data mínima de publicação (ex.: do backend) para exibir intervalo no preset TOTAL */
  totalSinceDate?: string | null;
}

export function FiltroPorPeriodo({
  value,
  onChange,
  label = 'FILTRAR POR:',
  applyOnClose = true,
  compact = false,
  totalSinceDate = null,
}: FiltroPorPeriodoProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<FiltroPorPeriodoValue>(value);
  const [customStart, setCustomStart] = useState<Date>(() =>
    value.start ? parseISO(value.start) : startOfMonth(new Date())
  );
  const [customEnd, setCustomEnd] = useState<Date>(() =>
    value.end ? parseISO(value.end) : new Date()
  );
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [monthLeft, setMonthLeft] = useState<Date>(() => customStart);

  const today = useMemo(() => new Date(), []);

  const displayText = useMemo(() => {
    const isTotal = value.preset === 'total' || (!value.start && !value.end);
    if (isTotal) {
      const endLabel = format(today, 'dd/MM/yyyy', { locale: ptBR });
      if (totalSinceDate) {
        return `TOTAL  ${formatDisplay(totalSinceDate)} - ${endLabel}`;
      }
      return 'TOTAL';
    }
    const startLabel = formatDisplay(value.start);
    const endLabel = formatDisplay(value.end);
    // Só um lado do período (o usuário removeu o outro chip): range aberto,
    // sem preset — o intervalo não corresponde mais a "este mês" etc.
    if (!startLabel) return `Até ${endLabel}`;
    if (!endLabel) return `A partir de ${startLabel}`;
    const presetLabel = value.preset && value.preset !== 'personalizado' ? PRESET_CONFIG.find((p) => p.key === value.preset)?.label : null;
    if (presetLabel) return `${presetLabel}  ${startLabel} - ${endLabel}`;
    return `${startLabel} - ${endLabel}`;
  }, [value, today, totalSinceDate]);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setPending({
      ...value,
      preset: value.preset ?? (!value.start || !value.end ? 'total' : 'personalizado'),
    });
    if (value.start) setCustomStart(parseISO(value.start));
    if (value.end) setCustomEnd(parseISO(value.end));
    // Calendário único: abre no mês do início do período atual (ou mês corrente).
    setMonthLeft(value.start ? startOfMonth(parseISO(value.start)) : startOfMonth(today));
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePresetClick = (key: PresetKey) => {
    if (key === 'total') {
      setPending({ start: null, end: null, preset: 'total' });
      return;
    }
    if (key === 'personalizado') {
      setPending({ ...pending, preset: 'personalizado' });
      return;
    }
    const config = PRESET_CONFIG.find((c) => c.key === key);
    if (!config?.getRange) return;
    const range = config.getRange(today);
    if (range) {
      const next = { start: toYMD(range.start), end: toYMD(range.end), preset: key };
      setPending(next);
      if (!applyOnClose) onChange(next);
    }
  };

  const handleApply = () => {
    if (pending.preset === 'personalizado') {
      const start = rangeStart || customStart;
      const end = (rangeStart && !rangeEnd) ? rangeStart : (rangeEnd || customEnd);
      const [s, e] = isBefore(start, end) ? [start, end] : [end, start];
      onChange({ start: toYMD(s), end: toYMD(e), preset: 'personalizado' });
    } else {
      onChange(pending);
    }
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleDayClick = (d: Date) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
    } else {
      setRangeEnd(d);
      const [s, e] = isBefore(rangeStart, d) ? [rangeStart, d] : [d, rangeStart];
      setPending({ start: toYMD(s), end: toYMD(e), preset: 'personalizado' });
    }
  };

  const yearOptions = useMemo(() => {
    const maxYear = today.getFullYear();
    const fallbackMinYear = 1937;
    let minYear = fallbackMinYear;
    if (totalSinceDate) {
      const parsed = parseISO(totalSinceDate);
      if (!Number.isNaN(parsed.getTime())) {
        minYear = parsed.getFullYear();
      } else {
        const y = parseInt(totalSinceDate.slice(0, 4), 10);
        if (Number.isFinite(y)) minYear = y;
      }
    }
    if (minYear > maxYear) minYear = maxYear;
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [today, totalSinceDate]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: format(new Date(2020, i, 1), 'MMM', { locale: ptBR }),
      })),
    [],
  );

  const setCalendarMonth = (_side: 'left' | 'right', year: number, monthIndex: number) => {
    setMonthLeft(startOfMonth(new Date(year, monthIndex, 1)));
  };

  const renderMonth = (month: Date, side: 'left' | 'right', onMonthChange: (delta: number) => void) => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const days = eachDayOfInterval({ start, end });
    const firstDay = start.getDay();
    const emptyStart = firstDay === 0 ? 6 : firstDay - 1;
    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

    // Destaque do intervalo. Durante a seleção (só o start clicado) destaca
    // apenas esse dia — NÃO cai em customEnd, senão vira {start:20, end:10} e
    // isWithinInterval lança "Invalid interval", quebrando a página. Sempre
    // ordenado (min..max) e validado.
    let selStart: Date | null;
    let selEnd: Date | null;
    if (pending.preset === 'personalizado') {
      if (rangeStart && rangeEnd) {
        selStart = rangeStart;
        selEnd = rangeEnd;
      } else if (rangeStart) {
        selStart = rangeStart;
        selEnd = rangeStart;
      } else {
        selStart = customStart;
        selEnd = customEnd;
      }
    } else {
      selStart = null;
      selEnd = null;
    }
    const range =
      selStart && selEnd && isValid(selStart) && isValid(selEnd)
        ? isBefore(selEnd, selStart)
          ? { start: selEnd, end: selStart }
          : { start: selStart, end: selEnd }
        : null;

    return (
      <Box sx={{ minWidth: 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 0.5 }}>
          <IconButton size="small" onClick={() => onMonthChange(-1)} aria-label="Mês anterior">
            <ChevronLeft />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center' }}>
            <FormControl size="small" variant="standard" sx={{ minWidth: 52 }}>
              <Select
                value={month.getMonth()}
                onChange={(e) => setCalendarMonth(side, month.getFullYear(), Number(e.target.value))}
                disableUnderline
                sx={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value} dense>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" variant="standard" sx={{ minWidth: 64 }}>
              <Select
                value={month.getFullYear()}
                onChange={(e) => setCalendarMonth(side, Number(e.target.value), month.getMonth())}
                disableUnderline
                MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                sx={{ fontSize: '0.8125rem', fontWeight: 600 }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y} dense>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <IconButton size="small" onClick={() => onMonthChange(1)} aria-label="Próximo mês">
            <ChevronRight />
          </IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, textAlign: 'center' }}>
          {weekDays.map((d) => (
            <Typography key={d} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {d}
            </Typography>
          ))}
          {Array.from({ length: emptyStart }).map((_, i) => (
            <Box key={`e-${i}`} sx={{ height: 32 }} />
          ))}
          {days.map((d) => {
            const inRange = range && isWithinInterval(d, { start: range.start, end: range.end });
            const isStart = range && isSameDay(d, range.start);
            const isEnd = range && isSameDay(d, range.end);
            const isSelected = range && (isStart || isEnd);
            return (
              <Box
                key={d.getTime()}
                onClick={() => handleDayClick(d)}
                sx={{
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: isSelected ? theme.palette.primary.main : inRange ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                  color: isSelected ? theme.palette.primary.contrastText : 'text.primary',
                  '&:hover': { bgcolor: isSelected ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1) },
                }}
              >
                <Typography variant="body2">{format(d, 'd')}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.75 : 1 }}>
        {label ? (
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={compact ? { fontSize: 12, textTransform: 'uppercase' } : undefined}
          >
            {label}
          </Typography>
        ) : null}
        <Button
          variant="outlined"
          size={compact ? 'small' : 'medium'}
          onClick={handleOpen}
          endIcon={<ArrowDropDown />}
          startIcon={<CalendarToday fontSize={compact ? 'small' : 'medium'} />}
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            textTransform: 'none',
            minWidth: compact ? 200 : 280,
            maxWidth: compact ? 320 : undefined,
            justifyContent: 'space-between',
            fontSize: compact ? 11 : undefined,
            fontWeight: compact ? 600 : undefined,
            py: compact ? 0.25 : undefined,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'space-between', overflow: 'hidden' }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayText}
            </Box>
          </Box>
        </Button>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 560, maxWidth: 640, overflow: 'hidden', mt: 1 } } }}
      >
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', width: '100%', minHeight: 340 }}>
          <Box sx={{ width: 200, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <List dense disablePadding>
              {PRESET_CONFIG.map((preset) => (
                <ListItemButton
                  key={preset.key}
                  selected={pending.preset === preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main },
                  }}
                >
                  <Typography variant="body2" fontWeight={pending.preset === preset.key ? 600 : 400}>
                    {preset.label}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
            {pending.preset === 'personalizado' && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {renderMonth(monthLeft, 'left', (d) => setMonthLeft(addMonths(monthLeft, d)))}
              </Box>
            )}
            {pending.preset !== 'personalizado' && pending.preset !== 'total' && pending.start && pending.end && (
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                {formatDisplay(pending.start)} – {formatDisplay(pending.end)}
              </Typography>
            )}
            {pending.preset === 'total' && (
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                {totalSinceDate
                  ? `Todas as publicações desde ${formatDisplay(totalSinceDate)}.`
                  : 'Todas as publicações registradas (sem filtro de período).'}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button size="small" variant="contained" onClick={handleApply}>
            Aplicar
          </Button>
        </Box>
        </Paper>
      </Popover>
    </>
  );
}
